import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Loader2, X, Link2, Sparkles, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Visual hint for a confidence score — keeps the UI honest about
 *  how trustworthy each match is so the coordinator never reconciles
 *  a stranger by accident. */
function confidenceTone(score) {
  if (score >= 0.95) return { cls: "bg-[#E9F5EE] text-[#0E6B3F] border-[#B5D9C3]", label: "Definitive" };
  if (score >= 0.75) return { cls: "bg-[#EEF5FC] text-[#0A3D79] border-[#B7D1EA]", label: "High confidence" };
  return { cls: "bg-[#FFF6E0] text-[#8A6B00] border-[#F0E29A]", label: "Possible" };
}

/**
 * Coordinator modal that maps a standalone (orphan) intake to a real
 * Avida pipeline_id. On open, fetches `/reconcile-suggestions` and
 * renders 0-3 candidate matches above the manual entry field — clicking
 * one auto-fills the form and stamps a "Selected match" pill.
 */
export default function ReconcileModal({ open, draft, onClose, onConfirm }) {
  const [pipelineId, setPipelineId] = useState("");
  const [demographicsId, setDemographicsId] = useState("");
  const [fireWebhook, setFireWebhook] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState("");
  const [selectedDraftId, setSelectedDraftId] = useState("");

  useEffect(() => {
    if (!open) return;
    setPipelineId("");
    setDemographicsId("");
    setFireWebhook(true);
    setBusy(false);
    setErr("");
    setSuggestions([]);
    setSuggestError("");
    setSelectedDraftId("");

    if (!draft?.draft_id) return;
    setSuggestLoading(true);
    axios
      .get(`${API}/intake/drafts/${encodeURIComponent(draft.draft_id)}/reconcile-suggestions`)
      .then((res) => setSuggestions(res.data?.suggestions || []))
      .catch((e) => {
        setSuggestError(e?.response?.data?.detail || e?.message || "Could not load suggestions.");
      })
      .finally(() => setSuggestLoading(false));
  }, [open, draft?.draft_id]);

  const selectedSuggestion = useMemo(
    () => suggestions.find((s) => s.draft_id === selectedDraftId) || null,
    [suggestions, selectedDraftId],
  );

  if (!open || !draft) return null;

  const applySuggestion = (s) => {
    setPipelineId(s.pipeline_id || "");
    setDemographicsId(s.demographics_id || "");
    setSelectedDraftId(s.draft_id);
    setErr("");
  };

  const submit = async () => {
    if (!pipelineId.trim()) {
      setErr("Pipeline ID is required.");
      return;
    }
    setBusy(true); setErr("");
    try {
      await onConfirm({
        pipelineId: pipelineId.trim(),
        demographicsId: demographicsId.trim(),
        fireAvidaCompletion: fireWebhook,
      });
    } catch (e) {
      setErr(e?.message || "Reconcile failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0B1E3A]/40 flex items-center justify-center px-4 py-12"
      data-testid="reconcile-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275]">
              Reconcile orphan intake
            </div>
            <h2 className="font-display text-xl font-bold text-[#0B1E3A] mt-1">
              {draft.client_name || "(unnamed client)"}
            </h2>
            <div className="text-[12px] text-[#3A5275] mt-1">
              DOB {draft.client_dob || "—"} · Member ID {draft.client_member_id || "—"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-[#3A5275] hover:text-[#0B1E3A] disabled:opacity-50"
            data-testid="reconcile-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Suggestions panel */}
        <div className="mb-5" data-testid="reconcile-suggestions-panel">
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="w-4 h-4 text-[#0A3D79]" />
            <span className="text-[12px] uppercase tracking-[0.12em] font-semibold text-[#0A3D79]">
              Likely matches
            </span>
          </div>

          {suggestLoading && (
            <div
              className="flex items-center gap-2 text-[12.5px] text-[#3A5275] px-3 py-3 rounded-lg bg-[#F1F5F9]"
              data-testid="reconcile-suggestions-loading"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              Searching Avida pipelines…
            </div>
          )}

          {!suggestLoading && suggestError && (
            <div
              className="text-[12.5px] text-[#A42A2A] px-3 py-2 rounded-lg bg-[#FBECEC] border border-[#E6B8B8]"
              data-testid="reconcile-suggestions-error"
            >
              {suggestError}
            </div>
          )}

          {!suggestLoading && !suggestError && suggestions.length === 0 && (
            <div
              className="text-[12.5px] text-[#3A5275] px-3 py-3 rounded-lg bg-[#F1F5F9] border border-[#D6E3EF]"
              data-testid="reconcile-suggestions-empty"
            >
              No matching Avida pipelines found — enter the pipeline_id manually below.
            </div>
          )}

          {!suggestLoading && !suggestError && suggestions.length > 0 && (
            <ul className="space-y-2" data-testid="reconcile-suggestions-list">
              {suggestions.map((s) => {
                const tone = confidenceTone(s.confidence);
                const selected = selectedDraftId === s.draft_id;
                return (
                  <li key={s.draft_id}>
                    <button
                      type="button"
                      onClick={() => applySuggestion(s)}
                      disabled={busy}
                      className={[
                        "w-full text-left rounded-lg border px-3 py-2.5 transition",
                        selected
                          ? "border-[#0A3D79] bg-[#EEF5FC] ring-1 ring-[#0A3D79]"
                          : "border-[#D6E3EF] bg-white hover:border-[#0A3D79] hover:bg-[#F7FAFD]",
                        busy ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                      ].join(" ")}
                      data-testid={`reconcile-suggestion-${s.draft_id}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[14px] font-semibold text-[#0B1E3A] truncate">
                            {s.client_name || "(unnamed)"}
                            {selected && (
                              <CheckCircle2
                                className="inline w-4 h-4 text-[#0E6B3F] ml-1.5 -mt-0.5"
                                data-testid={`reconcile-suggestion-selected-${s.draft_id}`}
                              />
                            )}
                          </div>
                          <div className="text-[11.5px] text-[#3A5275] mt-0.5">
                            DOB {s.client_dob || "—"} · Member {s.client_member_id || "—"}
                            {s.agency_name ? ` · ${s.agency_name}` : ""}
                          </div>
                          <div className="text-[11.5px] text-[#0A3D79] mt-0.5 font-mono truncate">
                            {s.pipeline_id}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span
                            className={`text-[10.5px] font-semibold uppercase tracking-[0.08em] px-2 py-0.5 rounded-full border ${tone.cls}`}
                            data-testid={`reconcile-suggestion-confidence-${s.draft_id}`}
                          >
                            {tone.label} · {Math.round(s.confidence * 100)}%
                          </span>
                          {s.match_reasons?.length > 0 && (
                            <span className="text-[10.5px] text-[#3A5275] text-right max-w-[160px]">
                              {s.match_reasons.join(" · ")}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="space-y-3 mb-4">
          <label className="block">
            <span className="text-[12px] font-semibold text-[#0B1E3A]">
              Parent-app Pipeline ID <span className="text-[#A42A2A]">*</span>
              {selectedSuggestion && (
                <span className="ml-2 text-[10.5px] text-[#0E6B3F] font-normal">
                  · auto-filled from match
                </span>
              )}
            </span>
            <input
              type="text"
              value={pipelineId}
              onChange={(e) => { setPipelineId(e.target.value); setSelectedDraftId(""); }}
              placeholder="e.g. PIPE-Q2-2026-A1"
              disabled={busy}
              data-testid="reconcile-pipeline-id"
              className="mt-1 w-full rounded-lg border border-[#D6E3EF] px-3 py-2 text-[14px] focus:border-[#0A3D79] focus:outline-none focus:ring-1 focus:ring-[#0A3D79]"
            />
            <span className="text-[11px] text-[#3A5275] mt-1 block">
              Copy this from the parent app — must match an existing client pipeline.
            </span>
          </label>

          <label className="block">
            <span className="text-[12px] font-semibold text-[#0B1E3A]">
              Demographics ID <span className="text-[#3A5275] font-normal">(optional)</span>
            </span>
            <input
              type="text"
              value={demographicsId}
              onChange={(e) => setDemographicsId(e.target.value)}
              placeholder="e.g. DEMO-A1"
              disabled={busy}
              data-testid="reconcile-demographics-id"
              className="mt-1 w-full rounded-lg border border-[#D6E3EF] px-3 py-2 text-[14px] focus:border-[#0A3D79] focus:outline-none focus:ring-1 focus:ring-[#0A3D79]"
            />
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={fireWebhook}
              onChange={(e) => setFireWebhook(e.target.checked)}
              disabled={busy}
              data-testid="reconcile-fire-webhook"
            />
            <span className="text-[12.5px] text-[#3A5275]">
              Fire Avida completion webhook immediately
            </span>
          </label>
        </div>

        {err && (
          <div
            className="mb-4 rounded-lg border border-[#E6B8B8] bg-[#FBECEC] px-3 py-2 text-[12.5px] text-[#A42A2A]"
            data-testid="reconcile-error"
          >
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="btn-ghost"
            data-testid="reconcile-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !pipelineId.trim()}
            className="btn-primary"
            data-testid="reconcile-confirm"
          >
            {busy
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Reconciling…</>
              : <><Link2 className="w-4 h-4" /> Reconcile draft</>}
          </button>
        </div>
      </div>
    </div>
  );
}
